import React from "react"
import colors from "@constants/colors"
import { Info } from "@components/icons"
import { IconWrapper } from "@components/icons/utils"
import VerticalSpace from "@components/VerticalSpace"
import SelectInput from "@components/inputs/SelectInput"
import {
  getCarsMakeModelYearUrl,
  getCarsMakeModelYearTrimUrl,
} from "@utils/urls"
import { useRouter } from "next/router"
import { useVehicleSelectionHandlers } from "../MenuDesktop/hooks/useVehicleSelectionHandlers"
import { CarSelectorProps } from "../MenuDesktop/types"
import {
  Container,
  Body,
  Content,
  Filters,
  Filter,
  Label,
  Footer,
} from "./styles"

const CarSelector: React.FC<CarSelectorProps> = ({
  isOpen,
  cars,
  makeModelOrder = {},
  resetPicker,
  onResetPicker,
  onRequestCloseMenu,
  module = "powersports",
}) => {
  const router = useRouter()
  const layoutData = require("@data/layoutData")

  cars = cars.filter((car) => car.module == module)

  const [make, setMake] = React.useState<string | "">("")
  const [model, setModel] = React.useState<string | "">("")
  const [trim, setTrim] = React.useState<string | "">("")
  const [year, setYear] = React.useState<string | "">("")
  const [models, setModels] = React.useState<Array<any>>([])
  const [trims, setTrims] = React.useState<Array<any>>([])
  const [years, setYears] = React.useState<Array<any>>([])

  // Use the shared vehicle selection handlers hook
  const {
    makes,
    onSelectMake: onSelectMakeBase,
    onSelectModel: onSelectModelBase,
    onSelectTrim: onSelectTrimBase,
  } = useVehicleSelectionHandlers({
    cars,
    selectedMake: make,
    selectedModel: model,
    selectedTrim: trim,
    setMake,
    setModel,
    setTrim,
    setYear,
    setModels,
    setTrims,
    setYears,
    isModern: false,
  })

  // Wrap handlers to add focus behavior specific to Powersports
  const onSelectMake = (makeValue: string) => {
    onSelectMakeBase(makeValue)
    // Focus Model field after make selection
    setTimeout(() => {
      document.getElementById("Model")?.focus()
    }, 0)
  }

  const onSelectModel = (modelValue: string) => {
    onSelectModelBase(modelValue)
  }

  const onSelectTrim = (trimValue: string) => {
    onSelectTrimBase(trimValue)
    // Focus Year field after trim selection
    setTimeout(() => {
      document.getElementById("Year")?.focus()
    }, 0)
  }

  // Handle focus after model selection - focus Trim if trims are shown, otherwise Year
  React.useEffect(() => {
    if (model && trims.length > 0 && !trim) {
      // Model selected and trims are available but not yet selected - focus Trim
      setTimeout(() => {
        document.getElementById("Trim")?.focus()
      }, 0)
    } else if (model && trims.length === 0 && !year) {
      // Model selected and no trims - focus Year
      setTimeout(() => {
        document.getElementById("Year")?.focus()
      }, 0)
    }
  }, [model, trims.length, trim, year])

  const setCar = (event) => {
    if (onRequestCloseMenu) {
      onRequestCloseMenu()
    }
    window.scrollTo({ top: 20, behavior: "smooth" })

    if (trim) {
      router.push(getCarsMakeModelYearTrimUrl(make, model, year, trim))
    } else {
      router.push(getCarsMakeModelYearUrl(make, model, year))
    }
  }

  const reset = () => {
    setMake("")
    setModel("")
    setTrim("")

    setModels([])
    setTrims([])
    setYears([])
  }

  React.useEffect(() => {
    if (!isOpen || resetPicker) {
      reset()

      if (resetPicker && onResetPicker) {
        onResetPicker()
      }
    }
  }, [isOpen, resetPicker])

  return (
    <Container>
      <Body bgColor={colors.snow}>
        <Label fontColor={colors.obsidian}>
          <div>Shop by vehicle</div>
          <h1>Let's start that project</h1>
        </Label>
        <Content>
          <Filters>
            <Filter>
              <SelectInput
                name="Make"
                label="Make"
                value={make}
                options={makes}
                onChange={(e) => {
                  onSelectMake(e.target.value)
                }}
              />
            </Filter>
            <Filter disabled={!make}>
              <SelectInput
                name={"Model"}
                label={"Model"}
                value={model}
                options={models}
                onChange={(e) => {
                  onSelectModel(e.target.value)
                }}
              />
            </Filter>
            {trims?.length > 0 && (
              <Filter disabled={!make || !model}>
                <SelectInput
                  name={"Trim"}
                  label={"Trim"}
                  value={trim}
                  options={trims}
                  onChange={(e) => {
                    onSelectTrim(e.target.value)
                  }}
                />
              </Filter>
            )}
            <Filter
              disabled={
                trims?.length > 0 ? !make || !model || !trim : !make || !model
              }
            >
              <SelectInput
                name={"Year"}
                label={"Year"}
                value={year}
                options={years}
                onChange={(e) => {
                  setYear(e.target.value)
                }}
              />
            </Filter>
            <button
              disabled={make == "" || model == "" || year == ""}
              onClick={(e) => {
                setCar(e)
              }}
            >
              Build
            </button>
          </Filters>
        </Content>
      </Body>
      <VerticalSpace height={40} />
      <Footer>
        <IconWrapper>
          <Info />
        </IconWrapper>
        Don't see your vehicle? Give us a call 888-460-0640
      </Footer>
    </Container>
  )
}

export default CarSelector
